import { APP_BASE_HREF, isPlatformServer } from '@angular/common';
import { Inject, Injectable, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import {
  CombinedSearchResult,
  DataAccessExhibitorsFacade,
  DataAccessFairsFacade,
  exhibitorDirectorySearchLoadNextSucceeded,
  exhibitorDirectorySearchLoadSucceeded,
  exhibitorDirectorySearchOpened,
} from '@planone/bau365/data-access-fair-data';
import {
  DataAccessSearchFacade,
  SEARCH_SERVICE_CONFIG,
  SearchFilters,
  searchPageOpened,
  SearchResult,
  searchResultsLoadSucceeded,
  SearchServiceConfigInterface,
  SearchSortOrder,
} from '@planone/bau365/data-access-search';
import { BOT_REGEX } from '@planone/bau365/util-restricted-access';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { WindowRef } from '@planone/shared/util-browser-api';
import { delay, filter, merge, mergeAll, of, takeLast, windowToggle, withLatestFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import {
  CustomEventEntity,
  CustomEventEntitySchematic,
  CustomEventIglu,
} from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class SearchEffects {
  searchUsage$ = createEffect(() =>
    merge(
      // we only want to track discovery searches where the searchFilterModal is closed
      this.actions$.pipe(
        ofType(searchPageOpened),
        withLatestFrom(this.searchFacade.searchFilterModalOpen$),
        filter(([_action, searchFilterModalOpen]) => !searchFilterModalOpen),
        map(([action]) => ({
          ...action,
          pageType: 'discover',
        }))
      ),
      // we add the last discovery search from when the searchFilterModal was open
      this.actions$.pipe(
        ofType(searchPageOpened),
        windowToggle(this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => open)), () =>
          this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => !open))
        ),
        map((pageOpenedWhileModalOpen) =>
          pageOpenedWhileModalOpen.pipe(
            takeLast(1),
            map((action) => ({
              ...action,
              pageType: 'discover',
            }))
          )
        ),
        mergeAll()
      ),
      // we only want to track exhibitor searches where the searchFilterModal is closed
      this.actions$.pipe(
        ofType(exhibitorDirectorySearchOpened),
        withLatestFrom(this.searchFacade.searchFilterModalOpen$),
        filter(([_action, searchFilterModalOpen]) => !searchFilterModalOpen),
        map(([action]) => ({
          ...action,
          filters: {
            searchTerm: action.searchTerm,
          },
          pageType: 'exhibitors',
        }))
      ),
      // we add the last exhibitor search from when the searchFilterModal was open
      this.actions$.pipe(
        ofType(exhibitorDirectorySearchOpened),
        windowToggle(this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => open)), () =>
          this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => !open))
        ),
        map((pageOpenedWhileModalOpen) =>
          pageOpenedWhileModalOpen.pipe(
            takeLast(1),
            map((action) => ({
              ...action,
              filters: {
                searchTerm: action.searchTerm,
              },
              pageType: 'exhibitors',
            }))
          )
        ),
        mergeAll()
      )
    ).pipe(
      delay(0), // delay needed to skip to the next frame since the opened action is fired from the resolver and title and url are not updated yet
      withLatestFrom(
        this.fairsFacade.fairId$,
        this.authenticationFacade.excludeAnalytics$,
        this.authenticationFacade.authenticated$,
        this.fairsFacade.isBauInsights$
      ),
      filter(
        ([action, _, __, authenticated, isBauInsights]) =>
          authenticated ||
          action.type === exhibitorDirectorySearchOpened.type ||
          isBauInsights ||
          isPlatformServer(this.platformId) ||
          this.botRegex.test(this.windowRef.nativeWindow.navigator.userAgent)
      ),
      switchMap(([action, fairId, exclude_analytics]) =>
        of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.search,
              data: <any>(
                this.convertSearchRequestToSnowplowStructure(action.filters, fairId, exclude_analytics, action.pageType)
              ),
            },
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'view_search_results',
            search_term: action.filters?.searchTerm,
            page_location: this.baseHref.slice(0, -1) + this.router.url,
            context: fairId,
            page_type: action.pageType,
          })
        )
      )
    )
  );

  searchImpressions$ = createEffect(() =>
    merge(
      // we only want to track discovery searches where the searchFilterModal is closed
      this.actions$.pipe(
        ofType(searchResultsLoadSucceeded),
        withLatestFrom(this.searchFacade.searchFilterModalOpen$),
        filter(([_action, searchFilterModalOpen]) => !searchFilterModalOpen),
        map(([action]) => action)
      ),
      // we add the last discovery search from when the searchFilterModal was open
      // since this is the search displayed when closed
      this.actions$.pipe(
        ofType(searchResultsLoadSucceeded),
        windowToggle(this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => open)), () =>
          this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => !open))
        ),
        map((pageOpenedWhileModalOpen) => pageOpenedWhileModalOpen.pipe(takeLast(1))),
        mergeAll()
      )
    ).pipe(
      withLatestFrom(
        this.fairsFacade.fairId$,
        this.searchFacade.searchFilterModalOpen$,
        this.authenticationFacade.excludeAnalytics$
      ),
      filter(([_, __, searchFilterModalOpen]) => !searchFilterModalOpen),
      switchMap(([action, fairId, _, exclude_analytics]) =>
        of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.view_entity_impressions,
              data: {
                impressions: action.results.map(({ id, type }, index) => {
                  return { id, entity_type: type, position: index + this.pageToOffset(action.requestedFilters?.page) };
                }),
                context: fairId,
                page_type: 'discover',
                exclude_analytics: exclude_analytics,
              },
            },
            context: <any>action.results.map((entity) => this.entityToAnalyticsContextEntity(entity, fairId)),
          })
        )
      )
    )
  );

  exhibitorSearchImpressions$ = createEffect(() =>
    merge(
      // we only want to track exhibitor searches where the searchFilterModal is closed
      this.actions$.pipe(
        ofType(exhibitorDirectorySearchLoadSucceeded),
        withLatestFrom(this.searchFacade.searchFilterModalOpen$),
        filter(([_action, searchFilterModalOpen]) => !searchFilterModalOpen),
        map(([action]) => action)
      ),
      // we add the last exhibitor search from when the searchFilterModal was open
      // since this is the search displayed when closed
      this.actions$.pipe(
        ofType(exhibitorDirectorySearchLoadSucceeded),
        windowToggle(this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => open)), () =>
          this.searchFacade.searchFilterModalOpen$.pipe(filter((open) => !open))
        ),
        map((pageOpenedWhileModalOpen) => pageOpenedWhileModalOpen.pipe(takeLast(1))),
        mergeAll()
      )
    ).pipe(
      withLatestFrom(
        this.fairsFacade.fairId$,
        this.searchFacade.searchFilterModalOpen$,
        this.authenticationFacade.excludeAnalytics$
      ),
      filter(([_, __, searchFilterModalOpen]) => !searchFilterModalOpen),
      switchMap(([action, fairId, _, exclude_analytics]) =>
        of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.view_entity_impressions,
              data: {
                impressions: action.results.map((item, index) => {
                  if (item.type === 'EXHIBITOR') {
                    return { id: item.exhibitor.id, entity_type: item.type, position: index + 1 };
                  } else {
                    return {
                      id: item.brand.exhibitor?.id ?? item.brand.id,
                      entity_type: item.type,
                      position: index + 1,
                    };
                  }
                }),
                context: fairId,
                page_type: 'exhibitors',
                exclude_analytics: exclude_analytics,
              },
            },
            context: <any>action.results.map((item) => this.combinedEntityToAnalyticsContextEntity(item, fairId)),
          })
        )
      )
    )
  );

  completion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(exhibitorDirectorySearchLoadNextSucceeded),
      withLatestFrom(
        this.fairsFacade.fairId$,
        this.authenticationFacade.excludeAnalytics$,
        this.exhibitorsFacade.exhibitorDirectoryResults$
      ),
      switchMap(([_, fairId, exclude_analytics, results]) =>
        of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'load_more_entities',
            page_type: 'exhibitors',
            context: fairId,
          }),
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.view_entity_impressions,
              data: {
                impressions: results.map((item, index) => {
                  if (item.type === 'EXHIBITOR') {
                    return { id: item.exhibitor.id, entity_type: item.type, position: index + 1 };
                  } else {
                    return {
                      id: item.brand.exhibitor?.id ?? item.brand.id,
                      entity_type: item.type,
                      position: index + 1,
                    };
                  }
                }),
                context: fairId,
                page_type: 'exhibitors',
                exclude_analytics,
              },
            },
            context: <any>(
              results.map((entity) =>
                this.dataTransformationHelper.snowplow?.mapExtendedSearchResult(entity, fairId, true)
              )
            ),
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private authenticationFacade: AuthenticationFacade,
    private dataTransformationHelper: DataTransformationHelper,
    private router: Router,
    private fairsFacade: DataAccessFairsFacade,
    private searchFacade: DataAccessSearchFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    private windowRef: WindowRef,
    @Inject(BOT_REGEX) private botRegex: RegExp,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(SEARCH_SERVICE_CONFIG) private searchServiceConfig: SearchServiceConfigInterface,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  private convertSearchRequestToSnowplowStructure(
    filters: SearchFilters,
    fairId: string,
    excludeAnalytics: boolean,
    pageType = 'discover'
  ): CustomEventEntity {
    const searchData: CustomEventEntity = {
      context: fairId,
      exclude_analytics: excludeAnalytics,
      page_type: pageType,
      from: this.pageToOffset(filters.page),
      size: this.searchServiceConfig.itemsPerPage,
      with_personalization: true,
    };
    if (filters?.searchTerm) {
      searchData.search_string = filters.searchTerm;
    }
    if (filters?.searchTypes) {
      searchData.search_type = filters.searchTypes;
    }
    if (filters?.searchSort) {
      searchData.sort = this.convertSearchSortStringToObject(filters.searchSort);
    }
    if (filters?.stringFilter) {
      searchData.string_filter = filters.stringFilter;
    }
    if (filters?.dateFilter) {
      searchData.date_filter = filters.dateFilter;
    }
    return searchData;
  }

  private convertSearchSortStringToObject(searchSort: SearchSortOrder): {
    by: 'ALPHABETICAL' | 'DATE' | 'RECOMMENDED' | 'EXHIBITION-HALLS';
    order: 'ASC' | 'DESC';
  } {
    switch (searchSort) {
      case SearchSortOrder.recommended:
        return { by: 'RECOMMENDED', order: 'DESC' };
      case SearchSortOrder.alphabeticalAsc:
        return { by: 'ALPHABETICAL', order: 'ASC' };
      case SearchSortOrder.alphabeticalDesc:
        return { by: 'ALPHABETICAL', order: 'DESC' };
      case SearchSortOrder.dateAsc:
        return { by: 'DATE', order: 'ASC' };
      case SearchSortOrder.dateDesc:
        return { by: 'DATE', order: 'DESC' };
      case SearchSortOrder.exhibitionHallsAsc:
        return { by: 'EXHIBITION-HALLS', order: 'ASC' };
      case SearchSortOrder.exhibitionHallsDesc:
        return { by: 'EXHIBITION-HALLS', order: 'DESC' };
    }
  }

  private pageToOffset(page: string | undefined): number {
    return page ? (parseInt(<string>page) - 1) * this.searchServiceConfig.itemsPerPage : 0;
  }

  private entityToAnalyticsContextEntity(entity: SearchResult, fairId: string): CustomEventEntitySchematic {
    const baseData = {
      context: fairId,
      locale: this.locale,
    };

    switch (entity.type) {
      case 'EVENT':
        return {
          schema: CustomEventIglu.entity_event,
          data: {
            event_name: entity.name,
            event_id: entity.id,
            starts_at: entity.starts_at.toISOString(),
            ends_at: entity.ends_at.toISOString(),
            ...baseData,
          },
        };
      case 'EXHIBITOR':
        return {
          schema: CustomEventIglu.entity_exhibitor,
          data: { exhibitor_id: entity.id, exhibitor_name: entity.name, ...baseData },
        };
      case 'POST':
        return {
          schema: CustomEventIglu.entity_post,
          data: { post_id: entity.id, post_name: entity.name, ...baseData },
        };
      case 'PRODUCT':
        return {
          schema: CustomEventIglu.entity_product,
          data: {
            product_id: entity.id,
            product_name: entity.name,
            ...baseData,
            published_at: entity.published_at.toISOString(),
          },
        };
    }
  }

  private combinedEntityToAnalyticsContextEntity(
    entity: CombinedSearchResult,
    fairId: string
  ): CustomEventEntitySchematic {
    const baseData = {
      context: fairId,
      locale: this.locale,
    };

    switch (entity.type) {
      case 'EXHIBITOR':
        return {
          schema: CustomEventIglu.entity_exhibitor,
          data: { exhibitor_id: entity.exhibitor.id, exhibitor_name: entity.exhibitor.name, ...baseData },
        };
      case 'BRAND':
        return {
          schema: CustomEventIglu.entity_exhibitor,
          data: {
            exhibitor_id: entity.brand.exhibitor?.id ?? entity.brand.id,
            exhibitor_name: entity.brand.name,
            ...baseData,
          },
        };
    }
  }
}
